/**
 * useStrategyStatus — polls the operational telemetry endpoints we have:
 *   /api/v1/forex/no-trade-monitor  (RAM strategy version + per-pair gates)
 *   /api/v1/forex/health            (OANDA / paper-trading readiness)
 *   /api/v1/system/status           (execution mode + live flags + kill switch)
 *
 * No broker credentials. No live-trading mutation. Read-only.
 * Treats any non-2xx as a transient error and surfaces a clean
 * UNAVAILABLE state rather than tearing the UI.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiV1Base } from '../../lib/apiConfig';

export type DataQuality = 'LIVE' | 'LIMITED' | 'UNAVAILABLE';

export interface NoTradeMonitor {
  status?: string;
  strategy_version?: string;
  strategy_status?: string;
  pair_universe?: string[];
  per_pair_gate_counts?: Record<string, Record<string, number>>;
  last_evaluated_at?: string | null;
  last_signal_at?: string | null;
  last_trade_at?: string | null;
  expected_profile?: string | null;
  active_profile?: string | null;
  reason_codes?: string[];
  display_message?: string;
}

export interface ForexHealth {
  practice_url?: string;
  oanda_configured?: boolean;
  oanda_practice_connected?: boolean;
  oanda_practice_account_ok?: boolean;
  oanda_practice_pricing_ok?: boolean;
  forex_paper_trading_ready?: boolean;
  live_trading_blocked?: boolean;
}

export interface SystemStatus {
  execution_mode?: string;
  live_enabled?: boolean;
  live_allowed?: boolean;
  safety_lock?: boolean;
  kill_switch?: boolean;
  health_status?: string;
}

export interface StrategyStatusBundle {
  noTrade: NoTradeMonitor | null;
  health: ForexHealth | null;
  system: SystemStatus | null;
  fetchedAt: string;
  dataQuality: DataQuality;
}

const POLL_INTERVAL_MS = 60_000;

async function jget<T>(path: string): Promise<T | null> {
  try {
    const resp = await fetch(`${apiV1Base()}${path}`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!resp.ok) return null;
    const body = await resp.json();
    return (body?.data ?? body) as T;
  } catch {
    return null;
  }
}

export function useStrategyStatus() {
  const [bundle, setBundle] = useState<StrategyStatusBundle | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const firstLoadRef = useRef<boolean>(true);

  const load = useCallback(async () => {
    try {
      const [noTrade, health, system] = await Promise.all([
        jget<NoTradeMonitor>('/forex/no-trade-monitor'),
        jget<ForexHealth>('/forex/health'),
        jget<SystemStatus>('/system/status'),
      ]);
      let dataQuality: DataQuality = 'UNAVAILABLE';
      const sourcesPresent = [noTrade, health, system].filter((x) => x !== null).length;
      if (sourcesPresent === 3) dataQuality = 'LIVE';
      else if (sourcesPresent > 0) dataQuality = 'LIMITED';
      setBundle({
        noTrade,
        health,
        system,
        fetchedAt: new Date().toISOString(),
        dataQuality,
      });
      setError(null);
      if (firstLoadRef.current) {
        setLoading(false);
        firstLoadRef.current = false;
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load strategy status';
      if (firstLoadRef.current) {
        setLoading(false);
        firstLoadRef.current = false;
        setError(msg);
      }
      // Otherwise swallow transient errors to keep last good render.
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (!cancelled) await load();
    };
    tick();
    const id = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [load]);

  return { bundle, loading, error, refetch: load };
}
